import * as React from 'react';
import type { BackgroundProps } from './types';
import { useVideoAPI } from '@wix/editor-elements-common-utils/hooks';
import styles from './component.module.scss';
import classNames from 'clsx';
import { Image, initCustomElement } from '@wix/image';
import { Video } from '@wix/video';
import type { MediaContainerVideoAPI } from '@wix/editor-elements-types/components';
import { PlaySmall, PauseSmall } from '@wix/wix-ui-icons-common';
import { useVideoPlaybackState, getBackgroundImageStyle } from './utils';
import { useService } from '@wix/services-manager-react';
import { EnvironmentDefinition } from '@wix/viewer-service-environment/definition';

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initCustomElement();
      },
      {
        once: true,
      },
    );
  } else {
    initCustomElement();
  }
}

const Background: React.ForwardRefRenderFunction<
  MediaContainerVideoAPI,
  BackgroundProps
> = (props: BackgroundProps, compRef) => {
  const {
    id,
    className,
    image,
    videoMedia,
    loop,
    muted,
    autoplay,
    playbackRate,
    posterEffect,
    focalPoint,
    fittingType,
    qualityPolicy,
    a11y,
    showControls = true,
  } = props;

  const videoRef = useVideoAPI(compRef, !!videoMedia);
  const { isPlaying: isVideoPlaying, togglePlayPause: handlePlayPause } =
    useVideoPlaybackState(videoRef);
  const environment = useService(EnvironmentDefinition);
  const isMobile = environment?.deviceType
    ? environment.deviceType !== 'Desktop'
    : false;
  const effectiveAutoplay = isMobile ? autoplay : true;

  const imageStyle = React.useMemo(
    () => getBackgroundImageStyle(image?.uri),
    [image?.uri],
  );

  let ImageComponent: React.ReactNode = null;
  if (image) {
    if (fittingType === 'tile') {
      ImageComponent = (
        <div
          id={`bgImg_${id}`}
          className={styles['bg-img-tile']}
          style={imageStyle}
          data-motion-part={`BG_IMG ${id}`}
        />
      );
    } else {
      ImageComponent = (
        <Image
          {...image}
          id={`bgImg_${id}`}
          containerId={id}
          displayMode={fittingType || 'fill'}
          focalPoint={focalPoint}
          alt={image.alt || ''}
          quality={{ quality: image.quality }}
        />
      );
    }
  }

  const VideoComponent = videoMedia && (
    <div className={styles['bg-video-container']}>
      <Video
        videoMedia={videoMedia}
        qualityPolicy={qualityPolicy}
        loop={loop}
        muted={muted}
        autoplay={effectiveAutoplay}
        playbackRate={Number(playbackRate)}
        posterEffect={posterEffect}
        videoRef={videoRef}
        id={`bgVideo_${id}`}
        motionPart={`BG_IMG ${id}`}
        focalPoint={focalPoint}
      />
      {showControls && (
        <button
          className={styles['video-play-pause-button']}
          onClick={handlePlayPause}
          type="button"
        >
          <span className={styles['visually-hidden-text']}>
            {isVideoPlaying
              ? `Pause Background Video: ${videoMedia?.name}`
              : `Play Background Video: ${videoMedia?.name}`}
          </span>
          {isVideoPlaying ? <PauseSmall /> : <PlaySmall />}
        </button>
      )}
    </div>
  );

  const Media =
    ImageComponent || VideoComponent ? (
      <>
        <div className={styles.media} data-motion-part={`BG_MEDIA ${id}`}>
          {ImageComponent}
          {VideoComponent}
        </div>
      </>
    ) : null;

  return (
    <div
      className={classNames(
        className,
        styles.background,
        styles.mask,
        'background',
      )}
      id={id}
      data-motion-part={`BG_LAYER ${id}`}
      aria-label={a11y?.ariaLabel}
    >
      {Media}
    </div>
  );
};

export default React.forwardRef(Background);
